import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography
} from '@mui/material';
import VideocamIcon from '@mui/icons-material/Videocam';
import WarningIcon from '@mui/icons-material/Warning';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../api/axios';

const SimpleCallButton = ({
  targetUserId,
  targetUserName,
  bookingId = null,
  bookingType = 'therapist',
  disabled = false,
  variant = 'contained',
  size = 'medium'
}) => {
  const [isInitiating, setIsInitiating] = useState(false);
  const [showCleanupDialog, setShowCleanupDialog] = useState(false);
  const [isCleaningUp, setIsCleaningUp] = useState(false);
  const navigate = useNavigate();

  const forceCleanup = async () => {
    setIsCleaningUp(true);

    try {
      // Endpoint corrected: /video-call/force-cleanup was never registered,
      // so this dialog could never work. The server now takes the user id
      // from the token, so no body is needed — the old version read
      // localStorage.user, which was never set and threw a TypeError.
      const { data } = await api.post('/video-call/cleanup-stale-calls');

      toast.success(`Cleaned ${data.cleanedCount} stale call${data.cleanedCount === 1 ? '' : 's'}`);
      setShowCleanupDialog(false);

      setTimeout(() => initiateCall(), 500);
    } catch (error) {
      console.error('[CallButton] Cleanup error:', error.message);
      toast.error(error.response?.data?.error || 'Failed to clean up. Please try again.');
    } finally {
      setIsCleaningUp(false);
    }
  };

  const initiateCall = async () => {
    if (isInitiating || disabled) return;

    setIsInitiating(true);

    try {
      // bookingId is now sent through. Without it active_calls.booking_id stays
      // NULL, so ending the call never marks the booking completed and the
      // follow-up messages never fire. See audit #19.
      const { data } = await api.post('/video-call/initiate', {
        receiverId: parseInt(targetUserId, 10),
        callType: 'video',
        bookingType,
        bookingId: bookingId ? parseInt(bookingId, 10) : null
      });

      if (data.success && data.roomId) {
        toast.info(`Calling ${targetUserName}...`);
        navigate(`/video-chat/${data.roomId}`);
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      const payload = error.response?.data;

      if (payload?.code === 'CALL_IN_PROGRESS') {
        setShowCleanupDialog(true);
        return;
      }

      if (payload?.code === 'USER_OFFLINE') {
        toast.warning(`${targetUserName} is not online right now.`);
        return;
      }

      if (payload?.code === 'SOCKET_NOT_READY' || payload?.code === 'SOCKET_FUNCTIONS_UNAVAILABLE') {
        toast.warning('Video service is starting up. Please try again in a few seconds.');
        return;
      }

      console.error('[CallButton] Error:', error.message);
      toast.error(payload?.error || 'Failed to initiate call');
    } finally {
      setIsInitiating(false);
    }
  };

  if (!targetUserId) {
    return (
      <Button disabled variant="outlined" size={size}>
        No Client ID
      </Button>
    );
  }

  return (
    <>
      <Button
        onClick={initiateCall}
        disabled={disabled || isInitiating}
        variant={variant}
        size={size}
        startIcon={
          isInitiating ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            <VideocamIcon />
          )
        }
        sx={{
          borderRadius: '25px',
          textTransform: 'none',
          fontWeight: 600,
          background: 'linear-gradient(135deg, #4CAF50, #45a049)',
          color: 'white',
          '&:hover': {
            background: 'linear-gradient(135deg, #45a049, #388e3c)',
            transform: disabled ? 'none' : 'translateY(-2px)',
            boxShadow: disabled ? 'none' : '0 6px 16px rgba(0,0,0,0.2)'
          },
          '&:disabled': {
            background: '#e0e0e0',
            color: '#9e9e9e'
          },
          transition: 'all 0.3s ease',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
        }}
      >
        {isInitiating ? 'Calling...' : `Call ${targetUserName}`}
      </Button>

      <Dialog
        open={showCleanupDialog}
        onClose={() => !isCleaningUp && setShowCleanupDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningIcon color="warning" />
          Call Already Active
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            You have an active or pending call that needs to be cleared.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            This usually happens when a previous call didn't end properly. Clearing
            it only affects your own calls.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setShowCleanupDialog(false)}
            disabled={isCleaningUp}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={forceCleanup}
            disabled={isCleaningUp}
            variant="contained"
            color="primary"
            startIcon={isCleaningUp ? <CircularProgress size={16} /> : null}
          >
            {isCleaningUp ? 'Clearing...' : 'Clear & Retry'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SimpleCallButton;